/**
 * Normalize DSOK workspaces for Admin UI and save payloads.
 * Canonical shape: { data: Workspace[] }
 * `data` must always be an array (never a keyed object with stray keys like "devices").
 */

const STRAY_DATA_KEYS = new Set(["devices", "data"]);

export const TURNSTILE_OPEN_RELAY_MIN = 1;
export const TURNSTILE_OPEN_RELAY_MAX = 8;

/**
 * @param {unknown} value
 * @returns {number | null} Integer 1–8, or null if missing/invalid.
 */
export function coerceTurnstileOpenRelayNumber(value) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < TURNSTILE_OPEN_RELAY_MIN || n > TURNSTILE_OPEN_RELAY_MAX) {
    return null;
  }
  return n;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function defaultTurnstileOpenRelayNumber(value) {
  return coerceTurnstileOpenRelayNumber(value) ?? TURNSTILE_OPEN_RELAY_MIN;
}

/**
 * Turnstile devices previously stored a single `turnstileNumber`; migrate to `qrCodeIdIn` / `qrCodeIdOut`.
 * Ensures relay number fields (`openRelayNumber*`, `disableRelayNumber*`) default to integers 1–8.
 * @param {object} device
 * @returns {object}
 */
export function migrateTurnstileDeviceForUi(device) {
  if (!device || typeof device !== "object") return device;
  if (String(device.type ?? "").trim() !== "turnstile") return device;
  const rawAd = device.additionalData;
  const ad =
    rawAd && typeof rawAd === "object" ? { ...rawAd } : {};

  const legacy =
    typeof ad.turnstileNumber === "string" ? ad.turnstileNumber.trim() : "";
  const qrIn =
    typeof ad.qrCodeIdIn === "string" ? ad.qrCodeIdIn.trim() : "";
  if (legacy && !qrIn) {
    delete ad.turnstileNumber;
    ad.qrCodeIdIn = legacy;
  } else if ("turnstileNumber" in ad) {
    delete ad.turnstileNumber;
  }

  ad.openRelayNumberIn = defaultTurnstileOpenRelayNumber(ad.openRelayNumberIn);
  ad.openRelayNumberOut = defaultTurnstileOpenRelayNumber(
    ad.openRelayNumberOut,
  );
  ad.disableRelayNumberIn = defaultTurnstileOpenRelayNumber(
    ad.disableRelayNumberIn,
  );
  ad.disableRelayNumberOut = defaultTurnstileOpenRelayNumber(
    ad.disableRelayNumberOut,
  );

  return { ...device, additionalData: ad };
}

/**
 * Workspace device list: always an array of device objects in saved JSON.
 * @param {*} devices
 * @returns {object[]}
 */
export function normalizeWorkspaceDevices(devices) {
  if (devices == null) return [];
  if (Array.isArray(devices)) {
    return devices.filter((d) => d != null && typeof d === "object");
  }
  if (typeof devices === "object") {
    const keys = Object.keys(devices)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    return keys
      .map((k) => devices[k])
      .filter((d) => d != null && typeof d === "object");
  }
  return [];
}

function normalizeWorkspaceEntry(w) {
  if (!w || typeof w !== "object") return null;
  return {
    ...w,
    devices: normalizeWorkspaceDevices(w.devices),
  };
}

/**
 * Coerce workspaces.data (array or malformed object) into a clean array.
 * @param {*} data
 * @returns {object[]}
 */
export function normalizeWorkspacesDataArray(data) {
  if (data == null) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeWorkspaceEntry).filter(Boolean);
  }
  if (typeof data === "object") {
    const keys = Object.keys(data);
    const numericKeys = keys
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    if (numericKeys.length > 0) {
      return numericKeys
        .map((k) => normalizeWorkspaceEntry(data[k]))
        .filter(Boolean);
    }
    // No numeric keys: treat remaining keys as workspace slots (legacy), ignore known stray keys.
    const rest = keys.filter((k) => !STRAY_DATA_KEYS.has(k));
    const out = [];
    for (const k of rest) {
      const v = data[k];
      const entry = normalizeWorkspaceEntry(v);
      if (entry) out.push(entry);
    }
    return out;
  }
  return [];
}

/**
 * Single source of truth for loading/editing: { data: [] }.
 * Supports:
 * - correct array `data`
 * - malformed object `data` with "0","1",… and stray keys (e.g. "devices" at wrong level)
 * - legacy top-level keyed workspaces (numeric keys only) without `data`
 *
 * @param {object|null|undefined} raw
 * @returns {{ data: object[] }}
 */
export function normalizeWorkspacesForUi(raw) {
  if (!raw || typeof raw !== "object") {
    return { data: [] };
  }

  const hasDataKey = Object.prototype.hasOwnProperty.call(raw, "data");

  let dataSource;
  if (hasDataKey) {
    dataSource = raw.data;
  } else {
    dataSource = raw;
  }

  const data = normalizeWorkspacesDataArray(dataSource);

  return { data };
}

/**
 * Merge customer + root workspace snapshots (customer wins on key overlap).
 * @param {object|null|undefined} customerWs
 * @param {object|null|undefined} rootWs
 */
export function mergeWorkspacesSources(customerWs, rootWs) {
  if (
    customerWs &&
    typeof customerWs === "object" &&
    rootWs &&
    typeof rootWs === "object"
  ) {
    return { ...rootWs, ...customerWs };
  }
  if (customerWs && typeof customerWs === "object") return customerWs;
  if (rootWs && typeof rootWs === "object") return rootWs;
  return null;
}

/**
 * Index of a workspace inside raw `workspaces.data` (Formik path segment).
 * UI lists workspaces from {@link normalizeWorkspacesForUi} which may drop null entries,
 * so map index there can differ from the index in stored `data` — always resolve by uuid.
 *
 * @param {*} rawData `customer.workspaces.data` (array or numeric-keyed object)
 * @param {string|null|undefined} workspaceUuid
 * @returns {number} index or -1
 */
export function findWorkspaceDataIndexByUuid(rawData, workspaceUuid) {
  if (workspaceUuid == null || String(workspaceUuid).trim() === "") {
    return -1;
  }
  const id = String(workspaceUuid).trim();
  if (rawData == null) return -1;
  if (Array.isArray(rawData)) {
    return rawData.findIndex(
      (w) =>
        w != null &&
        typeof w === "object" &&
        String(w.uuid ?? "").trim() === id,
    );
  }
  if (typeof rawData === "object") {
    const keys = Object.keys(rawData)
      .filter((k) => /^\d+$/.test(k))
      .sort((a, b) => Number(a) - Number(b));
    for (const k of keys) {
      const w = rawData[k];
      if (
        w != null &&
        typeof w === "object" &&
        String(w.uuid ?? "").trim() === id
      ) {
        return Number(k);
      }
    }
  }
  return -1;
}

/**
 * Deduped `{ uuid, name }` rows for device workspace MUI Select (last duplicate uuid wins).
 * @param {object[]} entries Normalized workspace entries (`normalizeWorkspacesForUi(...).data`).
 * @returns {{ uuid: string, name: string }[]}
 */
export function workspaceEntriesToSelectOptions(entries) {
  if (!Array.isArray(entries)) return [];
  const byUuid = new Map();
  for (const w of entries) {
    if (w && typeof w === "object" && w.uuid) {
      const u = String(w.uuid);
      byUuid.set(u, {
        uuid: u,
        name:
          typeof w.name === "string" && w.name.trim() !== ""
            ? w.name.trim()
            : u,
      });
    }
  }
  return Array.from(byUuid.values());
}
